import React, { useState, useReducer, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { SignUpForm } from '@/components/auth/SignUpForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Building2, CheckCircle2, Clock, Shield, Star, Users, Zap, ExternalLink } from 'lucide-react';
import { callSupabaseFunction, supabase } from '@/lib/supabase';
import EssentialPlanModal from '@/components/ui/EssentialPlanModal';
import SignupProgressBar from '@/components/auth/SignupProgressBar';
import { getAppUrl } from '@/utils/domains';

// State machine types
enum SignupStep {
  IDLE = 'idle',
  VALIDATING = 'validating',
  TRACKING_INTENT = 'tracking_intent',
  CREATING_AUTH = 'creating_auth',
  WAITING_SESSION = 'waiting_session',
  CREATING_ORG = 'creating_org',
  CREATING_CHECKOUT = 'creating_checkout',
  REDIRECTING = 'redirecting',
  ERROR = 'error'
}

interface SignupState {
  step: SignupStep;
  progress: number;
  message: string;
  error: string | null;
  canRetry: boolean;
  userId: string | null;
  organizationId: string | null;
  abortController: AbortController | null;
}

type SignupAction = 
  | { type: 'START_SIGNUP' }
  | { type: 'ADVANCE_STEP'; step: SignupStep; message: string; progress: number }
  | { type: 'SET_USER_ID'; userId: string }
  | { type: 'SET_ORG_ID'; organizationId: string }
  | { type: 'SET_ERROR'; error: string; canRetry: boolean }
  | { type: 'RETRY_FROM_ERROR' }
  | { type: 'RESET' }
  | { type: 'SET_ABORT_CONTROLLER'; controller: AbortController | null };

const initialState: SignupState = {
  step: SignupStep.IDLE,
  progress: 0,
  message: '',
  error: null,
  canRetry: false,
  userId: null,
  organizationId: null,
  abortController: null
};

function signupReducer(state: SignupState, action: SignupAction): SignupState {
  switch (action.type) {
    case 'START_SIGNUP':
      return {
        ...state,
        step: SignupStep.VALIDATING,
        progress: 5,
        message: 'Validating your information...',
        error: null,
        canRetry: false
      };
    
    case 'ADVANCE_STEP':
      return {
        ...state,
        step: action.step,
        progress: action.progress,
        message: action.message,
        error: null
      };
    
    case 'SET_USER_ID':
      return {
        ...state,
        userId: action.userId
      };
    
    case 'SET_ORG_ID':
      return {
        ...state,
        organizationId: action.organizationId
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        step: SignupStep.ERROR,
        error: action.error,
        canRetry: action.canRetry,
        message: 'Something went wrong'
      };
    
    case 'RETRY_FROM_ERROR':
      return {
        ...state,
        step: SignupStep.VALIDATING,
        progress: 5,
        message: 'Retrying...',
        error: null,
        canRetry: false
      };
    
    case 'RESET':
      return initialState;
    
    case 'SET_ABORT_CONTROLLER':
      return {
        ...state,
        abortController: action.controller
      };
    
    default:
      return state;
  }
}

/**
 * Advanced Plan Signup Flow
 * 
 * This component provides a frictionless signup experience where users enter their information
 * once and are seamlessly redirected to Stripe checkout without re-entering data.
 * 
 * TECHNICAL REQUIREMENTS:
 * - Email confirmation MUST be DISABLED in Supabase Auth settings for immediate access
 * - CORS headers must be configured in Supabase Edge Functions for cross-origin requests
 * - sessionStorage persistence is used to maintain overlay state across component remounts
 * 
 * OVERLAY PERSISTENCE SOLUTION:
 * This component implements a bulletproof overlay persistence pattern to handle React component
 * remounting during Supabase auth state changes. The overlay uses sessionStorage to survive:
 * - Component remounts triggered by auth state changes
 * - App re-renders during authentication flow
 * - Browser refresh or navigation issues
 * 
 * SIGNUP FLOW:
 * 1. Validate form inputs
 * 2. Track signup intent (analytics)
 * 3. Create auth user via supabase.auth.signUp()
 * 4. Wait for session to be available (with retries)
 * 5. Create organization and user profile (via edge function with service_role key)
 * 6. Create Stripe checkout session
 * 7. Redirect to Stripe for payment
 * 
 * The edge function uses service_role key to bypass RLS policies, preventing
 * authentication-related errors during the signup process.
 */

const AdvancedSignup = () => {
  // State machine for signup process
  const [signupState, dispatch] = useReducer(signupReducer, initialState);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    companyName: ''
  });
  const [showEssentialPlanModal, setShowEssentialPlanModal] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  
  // Ref to track if component is mounted
  const isMountedRef = useRef(true);

  // Set signup in progress flag to prevent auth redirects
  React.useEffect(() => {
    if (signupState.step !== SignupStep.IDLE) {
      // Mark signup as in progress to prevent auth-based redirects
      sessionStorage.setItem('signup-in-progress', 'true');
      console.log('🔒 Signup in progress - preventing auth redirects');
    } else {
      // Clear the flag when signup is idle
      sessionStorage.removeItem('signup-in-progress');
    }

    // Clear flag on successful completion (when redirecting to Stripe)
    if (signupState.step === SignupStep.REDIRECTING) {
      setTimeout(() => {
        sessionStorage.removeItem('signup-in-progress');
        console.log('🔓 Signup completed - auth redirects allowed');
      }, 500);
    }
  }, [signupState.step]);
  
  // 1. Restore overlay state on mount (survives component remounts during auth changes)
  React.useEffect(() => {
    const inProgress = sessionStorage.getItem('signup-in-progress') === 'true';
    const overlayVisible = sessionStorage.getItem('signup-overlay-visible') === 'true';
    if (inProgress || overlayVisible) {
      setShowOverlay(true);
    }
  }, []);

  // 2. Persist overlay state changes to sessionStorage (survives remounts)
  React.useEffect(() => {
    if (showOverlay) {
      sessionStorage.setItem('signup-overlay-visible', 'true');
    } else {
      sessionStorage.removeItem('signup-overlay-visible');
    }
  }, [showOverlay]);

  // 3. Fallback protection - REMOVED to prevent race condition with restore effect
  // Cleanup is handled explicitly in error/success/reset paths instead

  // Global debug function for overlay testing (development only)
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      (window as any).setShowOverlay = setShowOverlay;
    }
  }, []);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (signupState.abortController) {
        signupState.abortController.abort();
      }
    };
  }, []);

  // Step-by-step signup execution function
  const executeSignupFlow = async (
    formData: any, 
    dispatch: React.Dispatch<SignupAction>, 
    abortController: AbortController
  ) => {
    console.log('🎯 EXECUTE SIGNUP FLOW STARTED');
    console.log('🎯 Component mounted:', isMountedRef.current);
    console.log('🎯 Abort signal status:', abortController.signal.aborted);
    
    const { email, firstName, lastName, companyName, password } = formData;
    
    console.log('🎯 Form data extracted:', { email, firstName, lastName, companyName, hasPassword: !!password });
    
    // Get UTM parameters
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source') || 'direct';
    const utmMedium = urlParams.get('utm_medium') || 'website';
    const utmCampaign = urlParams.get('utm_campaign') || 'business_trial';

    // Step 1: Track signup intent
    console.log('🎯 Step 1: Tracking signup intent...');
    console.log('🎯 About to call track-signup-intent with:', {
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      companyName: companyName.trim(),
      utmSource, utmMedium, utmCampaign
    });
    
    const intentResponse = await callSupabaseFunction('track-signup-intent', {
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      companyName: companyName.trim(),
      stripeCustomerId: null,
      signupSource: 'business_trial',
      utmSource: utmSource,
      utmMedium: utmMedium,
      utmCampaign: utmCampaign
    });

    if (abortController.signal.aborted) return;

    // Step 2: Create auth user
    dispatch({ 
      type: 'ADVANCE_STEP', 
      step: SignupStep.CREATING_AUTH, 
      message: 'Creating your account...', 
      progress: 30 
    });

    console.log('🎯 Step 2: Creating Supabase auth user...');
    const { data: authUser, error: authError } = await supabase.auth.signUp({
      email: email.trim(),
      password: password,
      options: {
        data: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          role: 'bizadmin'
        }
      }
    });

    if (authError) {
      if (authError.message.includes('already registered')) {
        throw new Error('An account with this email already exists. Please sign in instead or use a different email address.');
      }
      throw new Error(`Failed to create user account: ${authError.message}`);
    }

    if (!authUser.user) {
      throw new Error('No user returned from auth signup');
    }

    const userId = authUser.user.id;
    dispatch({ type: 'SET_USER_ID', userId });
    console.log('✅ Auth user created:', userId);

    if (abortController.signal.aborted) return;

    // Step 3: Wait for session
    dispatch({ 
      type: 'ADVANCE_STEP', 
      step: SignupStep.WAITING_SESSION, 
      message: 'Initializing your session...', 
      progress: 50 
    });

    console.log('🎯 Step 3: Verifying session availability...');
    let session = null;
    let retries = 0;
    const maxRetries = 5; // Increased from 3 to 5
    const retryDelay = 1000; // Increased from 500ms to 1000ms

    while (!session && retries < maxRetries && !abortController.signal.aborted) {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession) {
        session = currentSession;
        break;
      }
      retries++;
      if (retries < maxRetries) {
        console.log(`Session not ready, retrying... (${retries}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }

    if (!session) {
      throw new Error('Session not available after signup. Please check your email for confirmation link.');
    }
    console.log('✅ Session verified successfully');
    
    if (abortController.signal.aborted) {
      console.log('❌ Abort signal detected, stopping execution');
      return;
    }

    console.log('🎯 Proceeding to Step 4: Organization creation');

    // Step 4: Create organization
    dispatch({ 
      type: 'ADVANCE_STEP', 
      step: SignupStep.CREATING_ORG, 
      message: 'Setting up your organization...', 
      progress: 70 
    });

    console.log('🎯 Step 4: Creating organization and user profile...');
    console.log('Using session:', session?.access_token ? 'Token present' : 'No token');
    
    let orgResponse, orgError;
    try {
      console.log('📡 Calling create-organization edge function...');
      const result = await supabase.functions.invoke('create-organization', {
        body: {
          userId: userId,
          organizationName: companyName.trim(),
          userEmail: email.toLowerCase().trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim()
        }
      });
      orgResponse = result.data;
      orgError = result.error;
      console.log('📡 Edge function call completed:', { 
        hasResponse: !!orgResponse, 
        hasError: !!orgError,
        response: orgResponse,
        error: orgError 
      });
    } catch (exception) {
      console.error('🚨 Exception during edge function call:', exception);
      throw new Error(`Edge function call failed: ${exception.message}`);
    }

    if (orgError || orgResponse?.error) {
      const errorMessage = orgError?.message || orgResponse?.error || 'Failed to create organization';
      console.error('🚨 Organization creation failed:', {
        orgError,
        orgResponse,
        errorMessage,
        userId,
        sessionExists: !!session,
        sessionToken: session?.access_token ? 'Present' : 'Missing'
      });
      throw new Error(errorMessage);
    }

    if (!orgResponse?.organizationId) {
      throw new Error('No organization ID returned from create-organization function');
    }

    const organizationId = orgResponse.organizationId;
    dispatch({ type: 'SET_ORG_ID', organizationId });
    console.log('✅ Organization created:', organizationId);

    if (abortController.signal.aborted) return;

    // Step 5: Create checkout session
    dispatch({ 
      type: 'ADVANCE_STEP', 
      step: SignupStep.CREATING_CHECKOUT, 
      message: 'Preparing secure checkout...', 
      progress: 85 
    });

    console.log('🎯 Step 5: Creating checkout session...');
    const checkoutResponse = await callSupabaseFunction('create-checkout', {
      priceId: ADVANCED_PLAN_PRICE_ID,
      customerEmail: email.trim(),
      successUrl: `${getAppUrl()}/checkout-success?setup=new`,
      cancelUrl: `${getAppUrl()}/signup-cancelled`,
      organizationId: organizationId,
      metadata: {
        org_name: companyName.trim(),
        user_name: `${firstName.trim()} ${lastName.trim()}`,
        user_email: email.trim(),
        signup_source: 'advanced_plan',
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        organization_id: organizationId,
        user_id: userId
      },
      trialDays: TRIAL_DAYS,
      allowPromoCodes: true
    });

    if (!checkoutResponse || !checkoutResponse.url) {
      throw new Error(checkoutResponse?.error || 'Failed to create checkout session');
    }

    // Step 6: Store checkout session and redirect
    if (checkoutResponse.sessionId) {
      await supabase
        .from('organizations')
        .update({ checkout_session_id: checkoutResponse.sessionId })
        .eq('id', organizationId);
    }

    dispatch({ 
      type: 'ADVANCE_STEP', 
      step: SignupStep.REDIRECTING, 
      message: 'Redirecting to secure checkout...', 
      progress: 100 
    });

    console.log('✅ Checkout session created, redirecting...');

    // Brief delay then redirect (overlay shows success message)
    setTimeout(() => {
      if (!abortController.signal.aborted) {
        // Clear flags before redirect (user is now in Stripe flow)
        sessionStorage.removeItem('signup-in-progress');
        sessionStorage.removeItem('signup-overlay-visible');
        
        // Normal redirect - no popup blocker issues
        window.location.href = checkoutResponse.url;
      }
    }, 3000); // 3 seconds
  };
  
  // Email error state for form validation
  const [emailError, setEmailError] = useState<string | null>(null);
  
  // Fixed price ID for Advanced plan
  const ADVANCED_PLAN_PRICE_ID = 'price_1RW6zeL45zl3J0lpnflp8WRG';
  const TRIAL_DAYS = 0; // No trial for Advanced plan

  // Unified input handler
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      companyName: ''
    });
    setEmailError(null);
    // Hide overlay and clear flags when resetting
    setShowOverlay(false);
    dispatch({ type: 'RESET' });
    sessionStorage.removeItem('signup-in-progress');
    sessionStorage.removeItem('signup-overlay-visible');
  };

  // Retry signup from error state
  const retrySignup = async () => {
    if (signupState.step !== SignupStep.ERROR || !signupState.canRetry) {
      return;
    }

    dispatch({ type: 'RETRY_FROM_ERROR' });

    try {
      const abortController = new AbortController();
      dispatch({ type: 'SET_ABORT_CONTROLLER', controller: abortController });
      await executeSignupFlow(formData, dispatch, abortController);
    } catch (error: any) {
      if (!isMountedRef.current) return;
      
      // Clear flags on retry error
      sessionStorage.removeItem('signup-in-progress');
      sessionStorage.removeItem('signup-overlay-visible');
      
      const isRetryable = !error.message.includes('already registered');
      dispatch({ 
        type: 'SET_ERROR', 
        error: error.message, 
        canRetry: isRetryable 
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submissions
    if (signupState.step !== SignupStep.IDLE) {
      return;
    }

    // Show overlay and set sessionStorage flags for persistence across remounts
    setShowOverlay(true);
    sessionStorage.setItem('signup-in-progress', 'true');
    sessionStorage.setItem('signup-overlay-visible', 'true');

    // Start the signup process
    dispatch({ type: 'START_SIGNUP' });

    try {
      // Step 1: Validation
      if (formData.password.length < 8) {
        throw new Error('Password must be at least 8 characters');
      }
      
      if (formData.password !== formData.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Please enter a valid email address');
      }

      // Create abort controller for this signup attempt
      const abortController = new AbortController();
      dispatch({ type: 'SET_ABORT_CONTROLLER', controller: abortController });

      // Step 2: Track signup intent
      dispatch({ 
        type: 'ADVANCE_STEP', 
        step: SignupStep.TRACKING_INTENT, 
        message: 'Preparing your account...', 
        progress: 15 
      });

      if (!isMountedRef.current) return;

      console.log('🚀 About to call executeSignupFlow with:', {
        email: formData.email,
        companyName: formData.companyName,
        mounted: isMountedRef.current,
        aborted: abortController.signal.aborted
      });

      try {
        await executeSignupFlow(formData, dispatch, abortController);
        console.log('✅ executeSignupFlow completed successfully');
      } catch (flowError) {
        console.error('❌ executeSignupFlow failed:', flowError);
        throw flowError;
      }

    } catch (error: any) {
      if (!isMountedRef.current) return;
      
      // Hide overlay and clear flags on error
      setShowOverlay(false);
      sessionStorage.removeItem('signup-in-progress');
      sessionStorage.removeItem('signup-overlay-visible');
      
      const isRetryable = !error.message.includes('already registered');
      dispatch({ 
        type: 'SET_ERROR', 
        error: error.message, 
        canRetry: isRetryable 
      });

      if (error.message.includes('already registered')) {
        setEmailError('An account with this email already exists. Please sign in instead or use a different email address.');
        toast.error('Email Already Registered', {
          description: (
            <div className="space-y-2">
              <p>This email is already associated with an account.</p>
              <div className="flex gap-2 mt-2">
                <a 
                  href="/login" 
                  className="text-blue-600 hover:text-blue-800 underline text-sm"
                >
                  Sign in instead
                </a>
                <span className="text-gray-400">|</span>
                <button 
                  onClick={resetForm} 
                  className="text-blue-600 hover:text-blue-800 underline text-sm"
                >
                  Use different email
                </button>
              </div>
            </div>
          ),
          duration: 8000,
        });
      } else {
        setEmailError(null);
        toast.error('Unable to complete signup', {
          description: error.message || 'Please try again or contact support.'
        });
      }
    }
  };

  const benefits = [
    { icon: Users, text: 'Unlimited users & families' },
    { 
      icon: Zap, 
      text: 'All Essential Plan features included',
      isClickable: true,
      onClick: () => setShowEssentialPlanModal(true)
    },
    { icon: Shield, text: 'Secure & compliant' },
    { icon: Clock, text: 'Cancel anytime' }
  ] as Array<{
    icon: React.ElementType;
    text: string;
    isClickable?: boolean;
    onClick?: () => void;
  }>;

  // Progress bar deleted - using simple toast notification instead

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30 flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-[800px] h-[800px] bg-gradient-to-br from-[#FA8C00]/5 to-transparent rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-to-tl from-[#4ba3c7]/5 to-transparent rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="relative z-10 w-full max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid md:grid-cols-2 gap-8 items-start"
        >
          {/* Left side - Benefits */}
          <div className="space-y-6">
            <div>
              <Badge className="mb-4 bg-[#FA8C00]/10 text-[#FA8C00] border-[#FA8C00]/20">
                ADVANCED PLAN
              </Badge>
              <h1 className="text-4xl font-bold text-gray-900 mb-3">
                Start Your Advanced Plan
              </h1>
              <p className="text-xl text-gray-600">
                Join hundreds of thriving programs using Hivetivity to transform their operations.
              </p>
            </div>



            {/* Benefits list */}
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="flex items-center gap-4"
                >
                  <div className="w-12 h-12 bg-[#4ba3c7]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-6 h-6 text-[#4ba3c7]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="text-gray-700 font-medium">{benefit.text}</span>
                      {benefit.isClickable && (
                        <button
                          onClick={benefit.onClick}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-all duration-200 hover:shadow-sm"
                          aria-label="View Essential Plan features"
                        >
                          <span>View Features</span>
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {benefit.text === 'All Essential Plan features included' && (
                      <p className="text-sm text-gray-600 mt-1">
                        Plus, Hive360 website integration, 45 additional eSignature completions, form designer & expanded Ask AI tool usage.
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>


            {/* Testimonial */}
            <Card className="border-gray-200 shadow-sm">
              <CardContent className="pt-6">
                <p className="text-gray-600 italic mb-4">
                  "Hivetivity transformed our studio. Parents are more engaged, payments are automated, and we save hours every week."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#FA8C00] to-[#FA8C00]/80 rounded-full flex items-center justify-center text-white font-bold">
                    JD
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Jennifer Davis</p>
                    <p className="text-sm text-gray-600">Elite Dance Academy</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right side - Signup form */}
          <Card className="shadow-xl border-gray-200 relative">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Building2 className="w-6 h-6 text-[#FA8C00]" />
                Create Your Account
              </CardTitle>
              <CardDescription>
                
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SignUpForm
                formData={formData}
                onChange={handleInputChange}
                onSubmit={handleSubmit}
                loading={signupState.step !== SignupStep.IDLE}
                selectedPriceId={ADVANCED_PLAN_PRICE_ID}
                emailError={emailError}
              />
              
              {/* Security note */}
              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                  <Shield className="w-3 h-3" />
                  Secured by Stripe • 256-bit encryption
                </p>
              </div>
            </CardContent>

            {/* Loading Overlay - React controlled */}
            {showOverlay && (
              <div id="signup-overlay" className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="text-white text-xl font-semibold">
                  Processing...
                </div>
              </div>
            )}
          </Card>
        </motion.div>
        
        {/* Essential Plan Modal */}
        <EssentialPlanModal
          isOpen={showEssentialPlanModal}
          onClose={() => setShowEssentialPlanModal(false)}
        />
      </div>
    </div>
  );
};

export default AdvancedSignup;